import { DEFAULT_LOCALE, type AppLocale } from "@/lib/i18n/constants";
import { localizedPath, normalizeCanonicalPagePath } from "@/lib/seo/url";
import { ROUTE_REGISTRY } from "@/lib/routes/registry";

export type LanguageSwitcherLabels = Readonly<Record<AppLocale, string>>;

export type LanguageSwitcherLink = {
  readonly locale: AppLocale;
  readonly label: string;
  readonly href: string;
  readonly current: boolean;
};

type BuildLanguageSwitcherLinksInput = {
  readonly pathname: string;
  readonly currentLocale: AppLocale;
  readonly visibleLocales: readonly AppLocale[];
  readonly labels: LanguageSwitcherLabels;
};

export function buildLanguageSwitcherLinks({
  pathname,
  currentLocale,
  visibleLocales,
  labels,
}: BuildLanguageSwitcherLinksInput): LanguageSwitcherLink[] {
  const routePath = normalizeCanonicalPagePath(pathname);
  const route = ROUTE_REGISTRY.find((candidate) => candidate.path === routePath);
  const eligibleLocales = visibleLocales.filter((locale) => isLocaleEligibleForRoute(locale, routePath, route?.locales));

  return eligibleLocales.map((locale) => ({
    locale,
    label: labels[locale] ?? locale,
    href: localizedPath(locale, routePath),
    current: locale === currentLocale,
  }));
}

function isLocaleEligibleForRoute(locale: AppLocale, routePath: string, routeLocales: readonly AppLocale[] | undefined): boolean {
  if (routeLocales) {
    return routeLocales.includes(locale);
  }

  return locale === DEFAULT_LOCALE && routePath === normalizeCanonicalPagePath(routePath);
}
